<template>
  <b-container class="p-0">
    <b-row class="mb-3">
      <b-col>
        <div class="d-flex">
          <div class="flex-grow-1">
            <!-- export buttons -->
            <template v-if="!isCompanyProductsContext && canExport">
              <!-- Market products context -->
              <action-button
                :title="$t('search.label.exportResults')"
                :options="exportButtonOptions"
                :is-processing="isDownloading"
                data-test="export-button-options"
              ></action-button>
            </template>
          </div>

          <div class="py-2 pr-3" data-test="searchSummary">
            {{ getPaginationItemsCounter }}
          </div>

          <div v-if="!infinite && paginator.totalItems > paginatorPageSize">
            <!-- pagination -->
            <b-pagination
              class="mb-0"
              :value="paginatorPageIndex"
              :total-rows="paginator.totalItems"
              :per-page="paginatorPageSize"
              @change="changePage"
            ></b-pagination>
          </div>
        </div>
        <!-- end flex -->
      </b-col>
    </b-row>

    <!-- page size select -->
    <b-row v-if="!infinite" no-gutters class="mb-3">
      <b-col cols="11">
        <div class="text-right py-2 pr-3">
          {{ $t('search.label.perPage') }}
        </div>
      </b-col>
      <b-col cols="1">
        <div class="float-right w-100">
          <b-select v-model="paginatorPageSize">
            <option v-for="value in paginator_options" :key="value" :value="value" v-text="value"></option>
          </b-select>
        </div>
      </b-col>
    </b-row>

    <b-row no-gutters>
      <b-col class="overflow-auto" :class="{ 'fixed-height' : infinite }">
        <b-table
          class="search"
          primary-key="id"
          :items="searchResult.results"
          :fields="shownFields"
          :tbody-tr-class="['pointer']"
          :no-local-sorting="true"
          :sort-by="currentSortBy"
          :sort-desc="currentSortDesc"
          no-sort-reset
          @sort-changed="sortingChanged"
          @row-clicked="(item) => openProductDetails(item, null, true)"
          striped
          hover
          data-test="resultsTable"
        >
          <template v-slot:cell(isFollowed)="{ item }"
            ><!-- favourites / pit -->
            <span v-if="'isFollowed' in item && !item.isProductInTest" @click.stop.prevent="() => changeFollow(item)"
              ><followed-icon :is-followed="item.isFollowed"></followed-icon
            ></span>
            <span v-else-if="item.isProductInTest && isCompanyProductsContext">
              <pit-svg></pit-svg>
            </span>
          </template>
          <template v-slot:cell(actions)="{ item }"
            ><!-- pit -->
            <div v-if="item.isProductInTest">
              <div class="w-100" @click.stop.prevent="() => pitFuncHandler(item, 'delete')">
                <span class="text-red">
                  <i class="fas fa-times"></i>
                </span>
              </div>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <!-- pagination (hidden in infinite mode) -->
    <template v-if="!infinite && paginator.totalItems > paginatorPageSize">
      <b-row no-gutters class="mb-3">
        <b-col cols="12">
          <div class="float-right">
            <b-pagination
              class="mb-0"
              :value="paginatorPageIndex"
              :total-rows="paginator.totalItems"
              :per-page="paginatorPageSize"
              @change="changePage"
            ></b-pagination>
          </div>
        </b-col>
      </b-row>
    </template>

    <!-- infinite scroll sentinel -->
    <template v-if="infinite">
      <b-row no-gutters>
        <b-col class="text-center py-1">
          <b-spinner v-if="isLoadingMore" small></b-spinner>
        </b-col>
      </b-row>
      <div ref="infiniteSentinel" style="height: 1px"></div>
    </template>
  </b-container>
</template>
<script>
import PitSvg from '../Lib/PitSvg'
import ActionButton from '../Lib/ActionButton'
import FollowedIcon from '../Lib/FollowedIcon'
import { track } from '@/plugins/tracking'
import { hasExportPermission } from '@/utils/exportPermission'

export default {
  name: 'resultsTable',
  components: { PitSvg, FollowedIcon, ActionButton },
  props: {
    paginator: {},
    searchResult: {},
    sortingChanged: {},
    exportRequested: {},
    productsInTestHandler: {},
    shownFields: {},
    searching: {},
    isCompanyProductsContext: {
      type: Boolean,
      default: false,
    },
    isDownloading: {
      type: Boolean,
      default: false,
    },
    infinite: {
      type: Boolean,
      default: false,
    },
    isLoadingMore: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      paginator_options: [25, 100, 500, 1000],
      paginator_page_size: this.paginator.pageSize,
      paginator_page_index: this.paginator.pageIndex,
      observer: null,
    }
  },

  mounted() {
    if (this.infinite) {
      this.setupInfiniteObserver()
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },

  watch: {
    searching(newVal, oldVal) {
      if (this.infinite && oldVal && !newVal) {
        this.$nextTick(() => this.reconnectObserver())
      }
    },
  },

  methods: {
    exportData(includeSids) {
      this.$emit('export-requested', includeSids)
      // track.event is defensive (no-op on failure): runs after the emit so
      // export is never affected.
      track.event('Search', 'export', includeSids ? 'with-investment-options' : 'product')
    },
    pitFuncHandler(item, action) {
      this.$emit('products-in-test-handler', item, action)
    },
    changePage(value) {
      this.paginator_page_index = value
      this.$emit('on-change-page-index', value)
    },
    setupInfiniteObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.isLoadingMore && !this.searching) {
            this.$emit('load-more')
          }
        },
        { threshold: 0 }
      )
      this.$nextTick(() => {
        if (this.$refs.infiniteSentinel) {
          this.observer.observe(this.$refs.infiniteSentinel)
        }
      })
    },
    reconnectObserver() {
      if (this.observer && this.$refs.infiniteSentinel) {
        this.observer.unobserve(this.$refs.infiniteSentinel)
        this.observer.observe(this.$refs.infiniteSentinel)
      }
    },
  },

  computed: {
    // Reflect the CURRENT server-side sort (searchResult.sortBy, e.g. "-referenceDate") back into
    // b-table instead of a hardcoded initial value. With :no-local-sorting, b-table computes the
    // sort-changed ctx.sortDesc as the toggle of what these props currently say — leaving them
    // stuck at the initial 'referenceDate'/true meant the very next column click (and every
    // "descending" click thereafter) toggled off the wrong baseline, so the "-" prefix sent to the
    // API never matched what the user actually clicked.
    currentSortBy() {
      return (this.searchResult.sortBy || '').replace(/^-/, '') || 'referenceDate'
    },
    currentSortDesc() {
      return (this.searchResult.sortBy || '').startsWith('-')
    },
    getPaginationItemsCounter() {
      let start_counter = 1
      this.paginator_page_index = this.paginator.pageIndex
      if (this.paginator.totalItems == null || this.paginator.totalItems === 0) {
        start_counter = 0
      } else if (this.paginator.totalItems <= this.paginatorPageSize || this.paginator.pageIndex == 1) {
        start_counter = 1
      } else {
        start_counter = this.paginatorPageSize * (this.paginatorPageIndex - 1) + 1
      }
      return this.$t('search.label.shown', {
        start: start_counter,
        end: Math.min(this.paginatorPageSize * this.paginatorPageIndex, this.paginator.totalItems),
        of: this.paginator.totalItems,
      })
    },

    paginatorPageSize: {
      get() {
        return this.paginator_page_size
      },
      set(newValue) {
        this.paginator_page_size = newValue
        this.$emit('on-change-page-size', newValue)
      },
    },
    paginatorPageIndex: {
      get() {
        return this.paginator_page_index
      },
    },

    hasMore() {
      return this.searchResult.results.length < this.paginator.totalItems
    },

    canExport() {
      return hasExportPermission(this.$store.getters.getPermissions)
    },
    canExportAllRevisions: function () {
      return this.$store.getters.getPermissions.userPermissions.canExportAllRevisions
    },
    isInvestmentCertificates() {
      return this.$store.getters.getSelectedAssetClass === 'investment-certificates'
    },
    exportButtonOptions() {
      let options = []
      if (this.isDownloading) {
        options.push({ label: this.$t('search.label.exportResultsDownloading'), variant: 'warning' })
      } else if (this.isInvestmentCertificates) {
        // Investment Certificates have a single export (SID-based); keep one "Product" entry that
        // invokes the SID export.
        options.push({
          label: this.$t('search.label.exportResultsProduct'),
          callback: () => this.exportData(true),
          variant: 'primary',
        })
      } else {
        options.push({
          label: this.$t('search.label.exportResultsProduct'),
          callback: () => this.exportData(false),
          variant: 'primary',
        })
        options.push({
          label: this.$t('search.label.exportResultInvestmentOptions'),
          callback: () => this.exportData(true),
          variant: 'primary',
        })
      }
      return options
    },
  },
}
</script>
<style lang="scss" scoped>
.ls-span {
  color: #f58a48;
  font-weight: bold;
  padding: 0.75rem !important;
}

/deep/ .page-link {
  background-color: #e9ecef;
}

/deep/ input:not([type='checkbox']),
select {
  width: 100%;
}

/deep/ .page-item > a,
/deep/ .page-item > span {
  background-color: transparent !important;
}

/deep/ .page-item.active > a {
  background-color: var(--secondaryBGColor) !important;
  color: blue;
}

.text-red {
  color: red;
}
.fixed-height {
  height: calc(100vh - 260px);
}
</style>
